import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "@/lib/toast";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTick } from "@/context/LiveContext";
import { CATEGORIES, COMBO, CAT } from "@/lib/categories";
import { paymentsEnabled, payForPlan } from "@/lib/razorpay";
import { withDefaults } from "@/lib/pricing";
import { Check, Plus, Minus, ShieldCheck, Megaphone, Loader2 } from "lucide-react";

const INCLUDED = [
  "Unlimited photos and walkthrough videos",
  "Owner's phone & WhatsApp on your page",
  "24-month availability calendar",
  "Packages, menus and FAQs",
  "Inquiries in your panel with reply templates",
  "Add staff to your panel",
  "Renewal reminders by email and in-app",
  "Zero commission on every booking",
];

const FAQS = [
  ["Do you take a cut of my bookings?", "No. Families pay you directly. Your only cost is the plan for your category."],
  ["What happens when my plan ends?", "We remind you 60 days ahead and again closer to the date. If you don't renew, your page is hidden from search until you do — nothing is deleted."],
  ["I run a hall and also do catering. Do I need two plans?", "Either buy each category you list, or take the All-in-one plan which covers every category for one price."],
  ["How do I pay?", "Online through Razorpay — UPI, cards, net banking and wallets. Your plan starts the moment the payment is confirmed."],
];

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export default function Pricing() {
  const [p, setP] = useState(() => withDefaults({}));
  const [term, setTerm] = useState("12m");
  const [sel, setSel] = useState("banquet_hall");
  const [canPay, setCanPay] = useState(false);
  const [busy, setBusy] = useState(false);
  const [faq, setFaq] = useState(0);
  const { user } = useAuth();
  const nav = useNavigate();
  const tick = useTick();

  useEffect(() => { api.get("/config/pricing").then((r) => setP(withDefaults(r.data))).catch(() => {}); }, [tick]);
  useEffect(() => { paymentsEnabled().then(setCanPay); }, []);

  const cats = useMemo(() => p.categories || {}, [p]);
  const rows = useMemo(() => CATEGORIES.filter((c) => cats[c.key]), [cats]);
  const months = term === "6m" ? 6 : 12;
  const price = (k) => Number(cats[k]?.[term] || 0);
  const selected = sel === "combo_all" ? COMBO : CAT[sel];
  const selPrice = price(sel);
  const saving = (k) => { const six = Number(cats[k]?.["6m"] || 0), twelve = Number(cats[k]?.["12m"] || 0); return six && twelve ? Math.max(0, Math.round((1 - twelve / (six * 2)) * 100)) : 0; };
  const maxSave = Math.max(0, ...Object.keys(cats).map(saving));
  const comboSum = rows.reduce((n, c) => n + price(c.key), 0);
  const isOwner = user?.role === "venue_owner";

  const buy = async (plan) => {
    if (!user) { nav(`/register?role=venue_owner&plan=${plan}`); return; }
    if (!isOwner) { toast.info("Plans are bought from a business account."); return; }
    if (!canPay) { nav("/owner?tab=subscription"); return; }
    setBusy(true);
    try {
      const r = await payForPlan({ plan, months });
      toast.success(`Payment received — your plan runs till ${new Date(r.ends_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}`);
    } catch (e) {
      if (!e?.cancelled) toast.error(e?.response?.data?.detail ? formatApiErrorDetail(e.response.data.detail) : e.message);
    } finally { setBusy(false); }
  };

  const cta = (plan) => (!user ? "Create a business account" : isOwner ? (canPay ? `Pay ${inr(price(plan))} securely` : "Renew from your panel") : "Use a business account");

  return (
    <div>
      {/* Header */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 pt-14 md:pt-20 pb-10">
        <div className="grid lg:grid-cols-[1.3fr_1fr] gap-8 items-end">
          <div>
            <p className="text-[color:var(--gold)] font-semibold text-lg">Pricing for wedding businesses</p>
            <h1 className="font-display text-[44px] md:text-[72px] leading-[1] text-[color:var(--maroon)] mt-3">List for a season.<br/>Keep every booking.</h1>
          </div>
          <div>
            <p className="text-[color:var(--ink-2)] text-[17px] leading-relaxed">Pick your category and a 6 or 12-month plan. Families in your city find you, check your dates and call you directly — we never take a commission.</p>
            <div className="mt-6 inline-flex p-1 rounded-full bg-white border border-[color:var(--line)]" role="tablist" data-testid="pricing-term">
              {[["6m", "6 months"], ["12m", "12 months"]].map(([k, l]) => (
                <button key={k} role="tab" aria-selected={term === k} onClick={() => setTerm(k)} className={`relative px-5 py-2.5 rounded-full text-[15px] font-semibold transition-colors ${term === k ? "text-white" : "text-[color:var(--maroon)]"}`} data-testid={`term-${k}`}>
                  {term === k && <motion.span layoutId="term-pill" className="absolute inset-0 rounded-full bg-[color:var(--maroon)]" transition={{ type: "spring", damping: 26, stiffness: 320 }}/>}
                  <span className="relative">{l}{k === "12m" && maxSave > 0 && <span className={`ml-1.5 text-[12px] ${term === k ? "text-[color:var(--gold-2)]" : "text-emerald-700"}`}>save up to {maxSave}%</span>}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Rate card */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-[1.35fr_1fr] gap-6 lg:gap-10 items-start">
          <div className="min-w-0 rounded-[26px] bg-white border border-[color:var(--line)] p-2 md:p-3" data-testid="rate-card">
            {rows.map((c) => {
              const on = sel === c.key;
              return (
                <button key={c.key} onClick={() => setSel(c.key)} className={`w-full min-w-0 text-left flex items-center gap-3 sm:gap-4 px-3 sm:px-4 md:px-5 py-4 md:py-5 rounded-[20px] transition-colors ${on ? "bg-[color:var(--ivory)]" : "hover:bg-[color:var(--cream)]"}`} data-testid={`plan-${c.key}`} aria-pressed={on}>
                  <span className={`w-11 h-11 shrink-0 rounded-full flex items-center justify-center transition-colors ${on ? "bg-[color:var(--maroon)] text-[color:var(--gold-2)]" : "bg-[color:var(--ivory)] text-[color:var(--maroon)]"}`}><c.Icon className="w-5 h-5"/></span>
                  <span className="min-w-0 flex-1 sm:flex-initial">
                    <span className="block font-display text-[20px] sm:text-[22px] md:text-[24px] leading-tight text-[color:var(--maroon)]">{c.plural}</span>
                    <span className="block text-[14px] text-neutral-500 leading-tight truncate">{c.blurb}</span>
                  </span>
                  <span className="flex-1 mx-2 border-b-2 border-dotted border-[color:var(--line)] self-center min-w-[16px] hidden sm:block" aria-hidden/>
                  <span className="text-right shrink-0">
                    <AnimatePresence mode="popLayout" initial={false}>
                      <motion.span key={term + c.key} initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -12, opacity: 0 }} className="block font-num text-[22px] sm:text-[26px] md:text-[30px] leading-none text-[color:var(--ink)] whitespace-nowrap">{inr(price(c.key))}</motion.span>
                    </AnimatePresence>
                    <span className="block text-[12px] text-neutral-500 mt-1">for {months} months</span>
                  </span>
                </button>
              );
            })}
            {cats.combo_all && (
              <button onClick={() => setSel("combo_all")} className={`w-full text-left flex items-center gap-4 px-4 md:px-5 py-5 mt-1 rounded-[20px] border-2 border-dashed transition-colors ${sel === "combo_all" ? "border-[color:var(--maroon)] bg-[color:var(--ivory)]" : "border-[color:var(--line)] hover:bg-[color:var(--cream)]"}`} data-testid="plan-combo_all">
                <span className="w-11 h-11 shrink-0 rounded-full bg-[color:var(--gold-2)] text-[color:var(--maroon-2)] flex items-center justify-center"><COMBO.Icon className="w-5 h-5"/></span>
                <span className="min-w-0 flex-1">
                  <span className="block font-display text-[24px] leading-tight text-[color:var(--maroon)]">All-in-one</span>
                  <span className="block text-[14px] text-neutral-500 leading-tight">Every category on one plan{comboSum > price("combo_all") ? ` · ${inr(comboSum - price("combo_all"))} less than buying each` : ""}</span>
                </span>
                <span className="text-right shrink-0">
                  <span className="block font-num text-[30px] leading-none text-[color:var(--maroon)]">{inr(price("combo_all"))}</span>
                  <span className="block text-[12px] text-neutral-500 mt-1">for {months} months</span>
                </span>
              </button>
            )}
          </div>

          {/* Selected plan */}
          <div className="lg:sticky lg:top-24">
            <div className="rounded-[26px] bg-[color:var(--maroon-2)] text-[#FBF7F4] p-7 md:p-9 relative overflow-hidden" data-testid="plan-summary">
              <div className="absolute -right-24 -top-24 w-72 h-72 rounded-full rings opacity-70 pointer-events-none" aria-hidden/>
              <div className="relative">
                <div className="flex items-center gap-2 text-[color:var(--gold-2)] font-semibold">{selected?.Icon && <selected.Icon className="w-4 h-4"/>} {sel === "combo_all" ? "All-in-one plan" : `${selected?.plural} plan`}</div>
                <AnimatePresence mode="wait" initial={false}>
                  <motion.div key={sel + term} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.22 }}>
                    <div className="font-num text-[64px] md:text-[80px] leading-none mt-4 text-white">{inr(selPrice)}</div>
                    <div className="text-[#FBF7F4]/75 mt-2 text-[15px]">for {months} months · about {inr(Math.round(selPrice / months))} a month</div>
                    {term === "12m" && saving(sel) > 0 && <div className="mt-3 inline-flex text-[13px] font-semibold px-3 py-1 rounded-full bg-[color:var(--gold-2)] text-[color:var(--maroon-2)]">{saving(sel)}% less than two 6-month plans</div>}
                  </motion.div>
                </AnimatePresence>
                <ul className="mt-7 grid gap-2.5 text-[15px]">
                  {INCLUDED.slice(0, 6).map((f) => <li key={f} className="flex items-start gap-2.5"><Check className="w-4 h-4 mt-1 shrink-0 text-[color:var(--gold-2)]"/> {f}</li>)}
                  {sel === "combo_all" && <li className="flex items-start gap-2.5"><Check className="w-4 h-4 mt-1 shrink-0 text-[color:var(--gold-2)]"/> All {rows.length} categories covered</li>}
                </ul>
                <button disabled={busy} onClick={() => buy(sel)} className="btn-gold w-full mt-8 !py-4 text-[16px]" data-testid={`plan-cta-${sel}`}>
                  {busy ? <><Loader2 className="w-4 h-4 animate-spin"/> Opening Razorpay…</> : cta(sel)}
                </button>
                <div className="mt-3 text-center text-[13px] text-[#FBF7F4]/60 inline-flex items-center gap-1.5 w-full justify-center"><ShieldCheck className="w-3.5 h-3.5"/> Secure payment by Razorpay · UPI, cards, net banking</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Premium spotlight */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 pb-16">
        <div className="rounded-[26px] border border-[color:var(--line)] bg-white p-7 md:p-12 grid lg:grid-cols-[1.4fr_1fr] gap-8 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-[color:var(--gold)] font-semibold"><Megaphone className="w-4 h-4"/> Premium spotlight</div>
            <h2 className="font-display text-3xl md:text-5xl mt-2 text-[color:var(--maroon)] leading-tight">Be one of the ten names every family in your city sees first.</h2>
            <p className="text-[color:var(--ink-2)] mt-3 text-[16px] max-w-xl">Ten slots per city, rotating at the top of the home page and the listings page. Add it to any live listing.</p>
          </div>
          <div className="rounded-[22px] bg-[color:var(--ivory)] p-7 text-center">
            <div className="text-[14px] text-[color:var(--ink-2)] font-semibold">Per listing, per city</div>
            <div className="font-num text-[56px] leading-none text-[color:var(--maroon)] mt-3">{inr(p.premium_slot?.[term])}</div>
            <div className="text-[14px] text-neutral-500 mt-2">for {months} months</div>
            <Link to={isOwner ? "/owner?tab=subscription" : "/register?role=venue_owner"} className="btn-wine mt-6 w-full" data-testid="premium-cta">{isOwner ? "Choose a listing to feature" : "Get started"}</Link>
          </div>
        </div>
      </section>

      {/* Included + FAQ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 pb-24 grid lg:grid-cols-2 gap-12">
        <div>
          <h2 className="font-display text-3xl md:text-4xl text-[color:var(--maroon)]">Every plan includes</h2>
          <ul className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-3.5 text-[16px] text-[color:var(--ink)]">
            {INCLUDED.map((f) => <li key={f} className="flex items-start gap-2.5"><Check className="w-5 h-5 mt-0.5 shrink-0 text-emerald-600"/> {f}</li>)}
          </ul>
        </div>
        <div>
          <h2 className="font-display text-3xl md:text-4xl text-[color:var(--maroon)]">Questions owners ask</h2>
          <div className="mt-4 divide-y divide-[color:var(--line)] border-y border-[color:var(--line)]">
            {FAQS.map(([q, a], i) => (
              <div key={q}>
                <button onClick={() => setFaq(faq === i ? -1 : i)} className="w-full text-left py-4 flex items-start gap-4" aria-expanded={faq === i}>
                  <span className="flex-1 font-semibold text-[17px] text-[color:var(--ink)]">{q}</span>
                  <span className="w-8 h-8 rounded-full border border-[color:var(--maroon)]/30 flex items-center justify-center text-[color:var(--maroon)] shrink-0">{faq === i ? <Minus className="w-4 h-4"/> : <Plus className="w-4 h-4"/>}</span>
                </button>
                <AnimatePresence initial={false}>
                  {faq === i && <motion.p initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden pb-4 pr-12 text-[color:var(--ink-2)] text-[16px] leading-relaxed">{a}</motion.p>}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
